import { asyncStorage, create, persist } from '@tinycld/core/lib/store'

interface WorkspaceStoreState {
    isSidebarOpen: boolean
    isDrawerOpen: boolean
    isMoreOpen: boolean
    isNotificationsOpen: boolean
    activePkgSlug: string | null
    // Per-package "last visited href" map. Packages may persist the
    // last deep-link the user opened (e.g. a calc file path) so the
    // sidebar/rail can re-link straight back to that file the next
    // time the package is reopened. Persisted across reloads.
    lastPackageHref: Record<string, string>
    toggleSidebar: () => void
    setSidebarOpen: (open: boolean) => void
    toggleDrawer: () => void
    setDrawerOpen: (open: boolean) => void
    setMoreOpen: (open: boolean) => void
    setNotificationsOpen: (open: boolean) => void
    setActivePkgSlug: (slug: string | null) => void
    setLastPackageHref: (slug: string, href: string) => void
    clearLastPackageHref: (slug: string) => void
}

export const useWorkspaceStore = create<WorkspaceStoreState>()(
    persist(
        set => ({
            isSidebarOpen: true,
            isDrawerOpen: false,
            isMoreOpen: false,
            isNotificationsOpen: false,
            activePkgSlug: null,
            lastPackageHref: {},

            toggleSidebar: () => set(s => ({ isSidebarOpen: !s.isSidebarOpen })),
            setSidebarOpen: open => set({ isSidebarOpen: open }),
            toggleDrawer: () => set(s => ({ isDrawerOpen: !s.isDrawerOpen })),
            setDrawerOpen: open => set({ isDrawerOpen: open }),
            setMoreOpen: open => set({ isMoreOpen: open }),
            setNotificationsOpen: open => set({ isNotificationsOpen: open }),
            setActivePkgSlug: slug => set({ activePkgSlug: slug }),
            setLastPackageHref: (slug, href) =>
                set(s => ({ lastPackageHref: { ...s.lastPackageHref, [slug]: href } })),
            clearLastPackageHref: slug =>
                set(s => {
                    if (!(slug in s.lastPackageHref)) return s
                    const next = { ...s.lastPackageHref }
                    delete next[slug]
                    return { lastPackageHref: next }
                }),
        }),
        {
            // Key kept as 'tinycld_sidebar_open' for historical reasons even
            // though it now also persists `lastPackageHref` (see partialize).
            // Renaming to something accurate (e.g. 'tinycld_workspace') would
            // orphan every existing user's stored sidebar/last-href state on
            // upgrade — Zustand's version/migrate can't carry state across a
            // key rename — and this state is trivial enough that the silent
            // drop isn't worth it. Left as-is intentionally.
            name: 'tinycld_sidebar_open',
            storage: asyncStorage,
            partialize: s => ({
                isSidebarOpen: s.isSidebarOpen,
                lastPackageHref: s.lastPackageHref,
            }),
        }
    )
)
